import classNames from 'classnames';
import InputField from 'components/InputField';
import SelectField from 'components/SelectField';
import { InventoryLocations } from 'models/Inventory';
import { Button, Loader } from 'ncoded-component-library';
import React, { useMemo } from 'react';
import { Form, FormProps } from 'react-final-form';
import { useTranslation } from 'react-i18next';
import formValidators from 'utils/formValidators';
import { SubmissionErrors } from 'final-form';
import api from 'api';
import useInventoryLocation from './hooks/useInventoryLocation';
import {
  customerFacingOptions,
  locationStatusOptions,
  locationUnitsOptions,
  sellingStatusOptions,
} from 'types';
import { useQuery } from '@tanstack/react-query';

import './InventoryLocationsForm.styles.scss';

const { required } = formValidators;

type InventoryLocationsFormProps = {
  loading?: boolean;
  className?: string;
  binNo?: number;
  isCopy?: boolean;
  storeNo?: number;
  onSubmit?: (
    values: InventoryLocations,
  ) => SubmissionErrors | Promise<SubmissionErrors> | void;
  handleCancel?: () => void;
} & FormProps<InventoryLocations>;

const InventoryLocationsForm = (props: InventoryLocationsFormProps) => {
  const {
    binNo,
    className,
    storeNo,
    isCopy = false,
    onSubmit,
    handleCancel,
  } = props;
  const { inventoryLocation, loading } = useInventoryLocation(binNo);

  const { t } = useTranslation();
  const classes = classNames('hhsa-manage-inventory-locations-form', className);

  const { data: types } = useQuery(
    ['locationTypes'],
    async () => {
      const { data } = await api.inventoryLocations.getLocationTypes();
      return data;
    },
    {
      refetchOnWindowFocus: false,
    },
  );

  const messages = useMemo(
    () => ({
      storeNumber: t('storeNumber'),
      binNo: t('InventoryLocations.binNo'),
      locationName: t('InventoryLocations.locationName'),
      locationSellingStatus: t('InventoryLocations.sellingStatus'),
      locationStatus: t('InventoryLocations.locationStatus'),
      type: t('InventoryLocations.type'),
      maxShelfCapacity: t('InventoryLocations.maxShelfCapacity'),
      cancel: t('General.cancel'),
      editLocation: t('InventoryLocations.edit'),
      addLocation: t('InventoryLocations.add'),
      copyLocation: t('InventoryLocations.copyLocation'),
      unit: t('InventoryLocations.unit'),
      customerFacing: t('InventoryLocations.customerFacing'),
    }),
    [t],
  );

  const unitsOptionsMapped = useMemo(
    () =>
      Object.values(
        locationUnitsOptions.map(({ value, label }) => ({
          label: t(`${label}`),
          value,
        })),
      ),
    [t],
  );

  const customerFacingOptionsMapped = useMemo(
    () =>
      Object.values(customerFacingOptions).map(({ value, label }) => ({
        label: t(`${label}`),
        value,
      })),
    [t],
  );

  const sellingStatusOptionsMapped = useMemo(
    () =>
      Object.values(sellingStatusOptions).map(({ value, label }) => ({
        label: t(`${label}`),
        value,
      })),
    [t],
  );

  const locationStatusOptionsMapped = useMemo(
    () =>
      Object.values(locationStatusOptions).map(({ value }) => ({
        label: t(`${value}`),
        value,
      })),
    [t],
  );

  const typeOptionsMapped = useMemo(
    () =>
      (types &&
        Object.values(types).map((type) => ({
          label: type.type,
          value: type.id,
        }))) ||
      [],
    [types],
  );

  const intialValues: Partial<InventoryLocations> = useMemo(
    () => ({
      storeNo: storeNo,
      binNo: !isCopy && inventoryLocation?.binNo,
      locationName: inventoryLocation?.locationName,
      locationSellingStatus: inventoryLocation?.locationSellingStatus,
      locationStatus: inventoryLocation?.locationStatus,
      maxShelfCapacity: inventoryLocation?.maxShelfCapacity,
      typeId: inventoryLocation?.typeId,
      units: inventoryLocation?.units,
      customerFacing: inventoryLocation?.customerFacing,
      minShelfCapacity: inventoryLocation?.minShelfCapacity,
    }),
    [
      inventoryLocation?.binNo,
      inventoryLocation?.customerFacing,
      inventoryLocation?.locationName,
      inventoryLocation?.locationSellingStatus,
      inventoryLocation?.locationStatus,
      inventoryLocation?.maxShelfCapacity,
      inventoryLocation?.minShelfCapacity,
      inventoryLocation?.typeId,
      inventoryLocation?.units,
      isCopy,
      storeNo,
    ],
  );

  const submitButtonText = useMemo(() => {
    if (isCopy) return messages.copyLocation;
    if (inventoryLocation) return messages.editLocation;
    return messages.addLocation;
  }, [
    inventoryLocation,
    isCopy,
    messages.addLocation,
    messages.copyLocation,
    messages.editLocation,
  ]);

  return (
    <Form
      onSubmit={onSubmit}
      initialValues={inventoryLocation ? intialValues : {}}
      render={(formRenderProps) => {
        const { handleSubmit, values } = formRenderProps;

        const {
          locationSellingStatus,
          units,
          typeId,
          locationStatus,
          customerFacing,
        } = values;

        return loading ? (
          <Loader color="#007cb9" />
        ) : (
          <form className={classes} onSubmit={handleSubmit}>
            <div
              className={classNames(
                'hhsa-manage-inventory-locations-form__fields',
                {
                  'hhsa-manage-inventory-locations-form__fields--grid': !isCopy,
                },
              )}
            >
              {!isCopy && (
                <InputField
                  name="storeNo"
                  label={messages.storeNumber}
                  initialValue={String(storeNo)}
                  validate={required(t('required'))}
                  disabled
                />
              )}
              <InputField
                type="number"
                name="binNo"
                label={messages.binNo}
                required
                validate={required(t('required'))}
                placeholder={messages.binNo}
                disabled={!isCopy && !!inventoryLocation}
              />
              {!isCopy && (
                <>
                  <InputField
                    type="text"
                    name="locationName"
                    label={messages.locationName}
                    required
                    validate={required(t('required'))}
                    placeholder={messages.locationName}
                  />
                  <SelectField
                    name="units"
                    options={unitsOptionsMapped}
                    label={messages.unit}
                    innerLabel={!units && t('selectAnOption')}
                    validate={required(t('required'))}
                  />
                  <SelectField
                    name="locationSellingStatus"
                    options={sellingStatusOptionsMapped}
                    innerLabel={!locationSellingStatus && t('selectAnOption')}
                    label={messages.locationSellingStatus}
                    validate={required(t('required'))}
                  />
                  <SelectField
                    name="locationStatus"
                    options={locationStatusOptionsMapped}
                    innerLabel={!locationStatus && t('selectAnOption')}
                    label={messages.locationStatus}
                    validate={required(t('required'))}
                  />
                  <SelectField
                    name="customerFacing"
                    options={customerFacingOptionsMapped}
                    innerLabel={!customerFacing && t('selectAnOption')}
                    label={messages.customerFacing}
                    validate={required(t('required'))}
                  />
                  <SelectField
                    name="typeId"
                    options={typeOptionsMapped}
                    label={messages.type}
                    innerLabel={!typeId && t('selectAnOption')}
                    validate={required(t('required'))}
                  />
                  <InputField
                    name="maxShelfCapacity"
                    disabled={!units || units === 'Multi'}
                    label={t('InventoryLocations.maxShelfCapacity')}
                    placeholder={t('InventoryLocations.maxShelfCapacity')}
                  />
                  <InputField
                    name="minShelfCapacity"
                    disabled={!units || units === 'Multi'}
                    label={t('minShelfCapacity')}
                    placeholder={t('minShelfCapacity')}
                  />
                </>
              )}
            </div>

            <div className="hhsa-manage-inventory-locations-form__actions">
              <Button
                type="button"
                onClick={handleCancel}
                variant="outline"
                className="hhsa-manage-inventory-locations-form__link"
              >
                <span>{messages.cancel} </span>
              </Button>

              <Button
                type="submit"
                variant="solid"
                className="hhsa-manage-inventory-locations-form__actions__submit"
              >
                {submitButtonText}
              </Button>
            </div>
          </form>
        );
      }}
    />
  );
};

export default InventoryLocationsForm;
